import React from 'react'
import { motion } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMobileAlt, faRobot, faPills, faUtensils, faHeartPulse } from '@fortawesome/free-solid-svg-icons'
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons'

const RedirectMobileApp = ({ feature = 'general' }) => {
  // Feature-specific content
  const getFeatureContent = () => {
    switch (feature.toLowerCase()) {
      case 'meal_tracker':
        return {
          icon: faUtensils,
          title: 'AI Meal Tracker',
          description:
            'Track calories in your meals using advanced AI technology. Get personalized nutrition guidance powered by artificial intelligence to help you maintain a healthy diet.',
          benefits: [
            'AI-powered calorie tracking',
            'Smart meal recognition',
            'Personalized nutrition insights',
            'Daily meal planning assistance'
          ],
          color: '#52c41a'
        }
      case 'medications':
        return {
          icon: faPills,
          title: 'Medication Tracker',
          description:
            'Manage your medications with ease. Track prescription refills, monitor adherence, and never miss a dose with smart reminders.',
          benefits: [
            'Prescription refill management',
            'Medication adherence tracking',
            'Smart dosage reminders',
            'Drug interaction warnings'
          ],
          color: '#722ed1'
        }
      default:
        return {
          icon: faHeartPulse,
          title: 'Advanced Health Features',
          description:
            'Access advanced health management tools and AI-powered insights with the Meta-Health ONE mobile app.',
          benefits: [
            'Comprehensive health tracking',
            'AI-powered insights',
            'Seamless mobile experience',
            'Advanced health tools'
          ],
          color: '#10b2e1'
        }
    }
  }

  const featureContent = getFeatureContent()

  const handleAppStoreClick = () => {
    // Replace with actual App Store URL
    window.open('https://apps.apple.com/app/meta-health-one', '_blank')
  }

  const handlePlayStoreClick = () => {
    // Replace with actual Play Store URL
    window.open('https://play.google.com/store/apps/details?id=com.metahealth.one', '_blank')
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          maxWidth: '480px',
          margin: '0 auto',
          textAlign: 'center'
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '32px 24px',
            marginBottom: '24px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              background: `linear-gradient(135deg, ${featureContent.color}, ${featureContent.color}cc)`,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '32px',
              color: 'white'
            }}
          >
            <FontAwesomeIcon icon={featureContent.icon} />
          </div>

          <h1
            style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 16px 0',
              background: `linear-gradient(135deg, ${featureContent.color}, ${featureContent.color}cc)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {featureContent.title}
          </h1>

          <p
            style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.6',
              margin: '0'
            }}
          >
            {featureContent.description}
          </p>
        </motion.div>

        {/* Features List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
          }}
        >
          <h3
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <FontAwesomeIcon icon={faRobot} style={{ color: featureContent.color }} />
            Key Features
          </h3>

          <div style={{ textAlign: 'left' }}>
            {featureContent.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 0',
                  borderBottom: index < featureContent.benefits.length - 1 ? '1px solid #f1f5f9' : 'none'
                }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    background: featureContent.color,
                    borderRadius: '50%',
                    flexShrink: 0
                  }}
                />
                <span
                  style={{
                    fontSize: '14px',
                    color: '#475569',
                    fontWeight: '500'
                  }}
                >
                  {benefit}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* App Download Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}
          >
            <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: '24px', color: featureContent.color }} />
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0'
              }}
            >
              Download Meta-Health ONE
            </h2>
          </div>

          <p
            style={{
              fontSize: '14px',
              color: '#64748b',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}
          >
            Get the full experience with our mobile app. Available for iOS and Android devices.
          </p>

          {/* Download Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAppStoreClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#000000',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '140px',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faApple} style={{ fontSize: '18px' }} />
              App Store
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePlayStoreClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#34a853',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '140px',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faGooglePlay} style={{ fontSize: '18px' }} />
              Play Store
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default RedirectMobileApp
